<template>
  <div class="page-container">
    <div class="page-title-container d-flex align-items-center">
      <router-link :to="{ name: 'vacation-requests-list' }" class="button button-light mr-3">
        <i class="ri-arrow-left-line"></i> Volver
      </router-link>
      <h1 class="page-title mb-0">Detalle de solicitud</h1>
    </div>

    <div v-if="request">
      <!-- Card: datos del empleado / contrato -->
      <b-card no-body class="custom-card mb-4">
        <b-card-header class="custom-card-header">
          <h3>Datos del colaborador</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div class="row">
            <div class="col-md-4">
              <div class="detail-label text-muted small">Nombre</div>
              <div class="detail-value font-weight-bold">{{ request.employeeName }}</div>
            </div>
            <div class="col-md-4">
              <div class="detail-label text-muted small">Empresa</div>
              <div class="detail-value">{{ request.companyName }}</div>
            </div>
            <div class="col-md-4">
              <div class="detail-label text-muted small">Área</div>
              <div class="detail-value">{{ request.areaName }}</div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Card: datos de la solicitud -->
      <b-card no-body class="custom-card mb-4">
        <b-card-header class="custom-card-header d-flex justify-content-between align-items-center">
          <h3 class="mb-0">Datos de la solicitud</h3>
          <span :class="['badge', getStatusColor(request.status)]">{{ request.status }}</span>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div class="row">
            <div class="col-md-3">
              <div class="detail-label text-muted small">Inicio período</div>
              <div class="detail-value">{{ request.startDate }}</div>
            </div>
            <div class="col-md-3">
              <div class="detail-label text-muted small">Fecha inicio vacaciones</div>
              <div class="detail-value">{{ request.startDate }}</div>
            </div>
            <div class="col-md-3">
              <div class="detail-label text-muted small">Fecha fin vacaciones</div>
              <div class="detail-value">{{ request.endDate }}</div>
            </div>
            <div class="col-md-3">
              <div class="detail-label text-muted small">Días calendario</div>
              <div class="detail-value font-weight-bold">{{ request.calendarDays }}</div>
            </div>
          </div>
          <div class="row mt-3" v-if="request.observations">
            <div class="col-12">
              <div class="detail-label text-muted small">Observaciones</div>
              <div class="detail-value">{{ request.observations }}</div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Card: historial de acciones -->
      <b-card no-body class="custom-card mb-4" v-if="hasHistory">
        <b-card-header class="custom-card-header">
          <h3>Historial de acciones</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div v-if="request.approvedBy" class="history-item mb-3">
            <i class="ri-checkbox-circle-line text-success mr-2"></i>
            <strong>Aprobado</strong> por {{ request.approvedBy }}
            <span v-if="request.approvedAt" class="text-muted ml-2">el {{ request.approvedAt }}</span>
          </div>
          <div v-if="request.rejectionReason" class="history-item mb-3">
            <i class="ri-close-circle-line text-danger mr-2"></i>
            <strong>Rechazado</strong> — Motivo: {{ request.rejectionReason }}
          </div>
          <div v-if="request.cancelledBy" class="history-item mb-3">
            <i class="ri-forbid-line text-warning mr-2"></i>
            <strong>Cancelado</strong> por {{ request.cancelledBy }}
            <span v-if="request.cancelledAt" class="text-muted ml-2">el {{ request.cancelledAt }}</span>
            <div v-if="request.cancellationReason" class="ml-4 text-muted small">
              Motivo: {{ request.cancellationReason }}
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Botones de acción -->
      <div class="d-flex justify-content-end mt-3">
        <button
          v-if="request.status === 'PENDIENTE'"
          type="button"
          class="button button-primary mr-2"
          @click="openApprove()"
        >
          <i class="ri-checkbox-circle-line mr-1"></i> Aprobar
        </button>
        <button
          v-if="request.status === 'PENDIENTE'"
          type="button"
          class="button button-danger mr-2"
          @click="openReject()"
        >
          <i class="ri-close-circle-line mr-1"></i> Rechazar
        </button>
        <button
          v-if="canCancel"
          type="button"
          class="button button-light"
          @click="openCancel()"
        >
          <i class="ri-forbid-line mr-1"></i> Cancelar solicitud
        </button>
      </div>
    </div>

    <!-- Modal: Aprobar -->
    <b-modal
      id="modal-approve-detail"
      title="Aprobar solicitud"
      @ok="confirmApprove"
      ok-title="Aprobar"
      cancel-title="Cancelar"
      ok-variant="primary"
    >
      <p>¿Confirma que desea aprobar esta solicitud de vacaciones?</p>
    </b-modal>

    <!-- Modal: Rechazar -->
    <b-modal
      id="modal-reject-detail"
      title="Rechazar solicitud"
      @ok="confirmReject"
      ok-title="Rechazar"
      cancel-title="Cancelar"
      ok-variant="danger"
      :ok-disabled="!rejectReason.trim()"
    >
      <div class="form-group">
        <label>Motivo de rechazo <span class="text-danger">*</span></label>
        <b-form-textarea v-model="rejectReason" rows="3" placeholder="Ingrese el motivo del rechazo" />
      </div>
    </b-modal>

    <!-- Modal: Cancelar -->
    <b-modal
      id="modal-cancel-detail"
      title="Cancelar solicitud"
      @ok="confirmCancel"
      ok-title="Cancelar solicitud"
      cancel-title="Volver"
      ok-variant="warning"
      :ok-disabled="!cancelReason.trim()"
    >
      <div class="form-group">
        <label>Motivo de cancelación <span class="text-danger">*</span></label>
        <b-form-textarea v-model="cancelReason" rows="3" placeholder="Ingrese el motivo de la cancelación" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { VacationService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'VacationRequestDetail',
  data() {
    return {
      request: null,
      rejectReason: '',
      cancelReason: '',
    }
  },
  computed: {
    hasHistory() {
      return this.request && (
        this.request.approvedBy ||
        this.request.rejectionReason ||
        this.request.cancelledBy
      )
    },
    canCancel() {
      if (!this.request || this.request.status !== 'APROBADA') return false
      const today = new Date().toISOString().split('T')[0]
      return this.request.startDate > today
    },
  },
  methods: {
    getStatusColor(status) {
      const found = Object.values(Constants.VacationRequestStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    openApprove() {
      this.$bvModal.show('modal-approve-detail')
    },
    openReject() {
      this.rejectReason = ''
      this.$bvModal.show('modal-reject-detail')
    },
    openCancel() {
      this.cancelReason = ''
      this.$bvModal.show('modal-cancel-detail')
    },
    async confirmApprove() {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.approveRequest(this.request.id)
        Alert.success('Solicitud aprobada correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al aprobar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmReject() {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.rejectRequest(this.request.id, { rejectionReason: this.rejectReason })
        Alert.success('Solicitud rechazada')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al rechazar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.cancelRequest(this.request.id, { cancellationReason: this.cancelReason })
        Alert.success('Solicitud cancelada')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getRequest(this.$route.params.id)
        this.request = resp.payload
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la solicitud')
        this.$router.push({ name: 'vacation-requests-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  created() {
    this.loadData()
  },
}
</script>
